import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { useReveal } from "@/hooks/useReveal";
import { submitLiveOasysLead, getLiveOasysConfig } from "@/lib/api";
import { UFS } from "@/data/formOptions";

// Campaign art — 1080x1920 poster fornecido pela produção.
const LIVE_ART =
  "https://customer-assets-lqy194kg.emergentagent.net/job_oasys-academy/artifacts/g0j1x2rj_Sem%20t%C3%ADtulo%20-%2005%20de%20julho%20de%202026%20%C3%A0s%2016.27.14-4%20%2834%29.png";

const LIVE_SOURCE = "live-oasys-10ago2026";

const PERFIL_OPTIONS = [
  { value: "auxiliar", label: "Auxiliar" },
  { value: "profissional", label: "Profissional" },
  { value: "dono", label: "Dono(a) / Gestor(a)" },
];

// Analytics — lightweight, dependency-free. Emits `dataLayer` events when
// present (GTM), else a no-op. Never blocks the UI.
function track(event, extra = {}) {
  try {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ...extra });
    }
  } catch (_) {
    /* noop */
  }
}

function useSEO() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Live Oasys Academy | Gilson Ribeiro + Luciano Marques";

    const setMeta = (attr, key, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
      return el;
    };

    const created = [];
    created.push(
      setMeta(
        "name",
        "description",
        "Crescimento não é só meta. É padrão. Participe gratuitamente da Live Oasys Academy, dia 10 de agosto às 20h.",
      ),
    );
    created.push(
      setMeta("property", "og:title", "LIVE OASYS ACADEMY — 10 AGO • 20H"),
    );
    created.push(
      setMeta(
        "property",
        "og:description",
        "Antes de mudar seus resultados, observe o que você repete.",
      ),
    );
    created.push(setMeta("property", "og:image", LIVE_ART));
    created.push(setMeta("property", "og:type", "website"));
    created.push(setMeta("name", "twitter:card", "summary_large_image"));

    return () => {
      document.title = prevTitle;
    };
  }, []);
}

function digitsOnly(s) {
  return (s || "").replace(/\D/g, "");
}

// Simple BR WhatsApp mask: (11) 98765-4321. Accepts either 10 or 11 digits
// (with area code). Not exhaustive; server does the authoritative validation.
function formatWhatsapp(raw) {
  const d = digitsOnly(raw).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10)
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LiveOasys() {
  const rootRef = useReveal();
  useSEO();

  const [formStartedTracked, setFormStartedTracked] = useState(false);
  const [form, setForm] = useState({
    nome: "",
    whatsapp: "",
    email: "",
    perfil: "",
    cidade: "",
    estado: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [liveUrl, setLiveUrl] = useState(null);
  const formRef = useRef(null);

  // Fire live_page_view once on mount.
  useEffect(() => {
    track("live_page_view", { source: LIVE_SOURCE });
  }, []);

  // Fetch the live broadcast URL when we render the confirmation, so the
  // button appears the moment the operator sets LIVE_10AGO_URL — no reload
  // required if the user keeps the tab open.
  useEffect(() => {
    if (!success) return;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const cfg = await getLiveOasysConfig();
        if (!cancelled) setLiveUrl(cfg?.live_url || null);
      } catch (_) {
        /* noop */
      }
    };
    fetchOnce();
    const iv = setInterval(fetchOnce, 30000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [success]);

  const scrollToForm = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const setField = (k, v) => {
    if (!formStartedTracked) {
      setFormStartedTracked(true);
      track("live_form_started", { source: LIVE_SOURCE });
    }
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.nome.trim().length < 2) e.nome = "Informe seu nome completo.";
    const wDigits = digitsOnly(form.whatsapp);
    if (wDigits.length < 10 || wDigits.length > 11)
      e.whatsapp = "WhatsApp inválido. Ex: (11) 98765-4321.";
    if (!EMAIL_RE.test(form.email)) e.email = "E-mail inválido.";
    if (!form.perfil) e.perfil = "Selecione um perfil.";
    if (form.cidade.trim().length < 2) e.cidade = "Informe sua cidade.";
    if (!form.estado) e.estado = "Selecione o estado.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    track("live_form_submitted", { source: LIVE_SOURCE });
    try {
      await submitLiveOasysLead({
        nome: form.nome.trim(),
        whatsapp: form.whatsapp.trim(),
        email: form.email.trim(),
        perfil: form.perfil,
        cidade: form.cidade.trim(),
        estado: form.estado,
      });
      setSuccess(true);
      track("live_registration_success", { source: LIVE_SOURCE });
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch (err) {
      setErrors({
        _submit:
          err?.detail === "invalid_whatsapp"
            ? "WhatsApp inválido."
            : "Não foi possível concluir sua inscrição. Tente novamente.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================
  // Success / Confirmation view
  // ============================================================
  if (success) {
    return (
      <div ref={rootRef} data-testid="live-confirmado-page" className="bg-white">
        <Navbar />
        <main className="pt-32 md:pt-40 pb-24 md:pb-32">
          <section className="max-w-[1100px] mx-auto px-6 md:px-10">
            <div className="reveal">
              <div className="flex items-center gap-4 mb-8">
                <span className="rule-yellow" />
                <span
                  className="eyebrow"
                  data-testid="live-confirmado-eyebrow"
                >
                  Inscrição confirmada
                </span>
              </div>
              <h1
                data-testid="live-confirmado-title"
                className="font-display text-4xl md:text-6xl lg:text-[4.2rem] leading-[1.02] tracking-tight text-[#0a0a0a]"
              >
                Nos vemos <em className="italic">ao vivo.</em>
              </h1>
              <div className="mt-10 max-w-2xl space-y-5 text-lg md:text-xl leading-[1.5] text-[#2a2a2a]">
                <p>
                  <em className="italic">10 de agosto</em>, às{" "}
                  <span className="font-medium">20h</span> — horário de Brasília.
                </p>
                <p className="uppercase tracking-[0.08em] text-sm text-[#0a0a0a]/70 font-medium">
                  Gilson Ribeiro + Luciano Marques
                </p>
                <p className="italic text-[#0a0a0a]">
                  Antes de mudar seus resultados, observe o que você repete.
                </p>
              </div>

              {/* Access block */}
              <div
                data-testid="live-access-block"
                className="mt-12 md:mt-16 border-t border-[#0a0a0a]/15 pt-8"
              >
                <div className="text-xs uppercase tracking-[0.14em] text-[#0a0a0a]/60 mb-4">
                  Acessar a live
                </div>
                {liveUrl ? (
                  <a
                    data-testid="live-enter-btn"
                    href={liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      track("live_access_clicked", { source: LIVE_SOURCE })
                    }
                    className="inline-flex items-center gap-3 px-8 py-4 bg-[#0a0a0a] text-white font-medium tracking-wide hover:bg-[#F5B301] hover:text-[#0a0a0a] transition-colors duration-200"
                  >
                    ENTRAR NA LIVE
                    <span aria-hidden="true">→</span>
                  </a>
                ) : (
                  <p
                    data-testid="live-not-ready"
                    className="text-[#0a0a0a]/70 text-base"
                  >
                    O acesso será liberado aqui antes do início da transmissão.
                  </p>
                )}
              </div>

              {/* Secondary — Presencial */}
              <div
                data-testid="live-macd-cross"
                className="mt-16 md:mt-24 border-t border-[#0a0a0a]/10 pt-10"
              >
                <div className="text-xs uppercase tracking-[0.14em] text-[#0a0a0a]/50 mb-3">
                  Essa conversa continua.
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-[#0a0a0a] mb-2">
                  MACD Connect Presencial
                </h3>
                <p className="text-[#0a0a0a]/70 text-base mb-6">
                  31 de agosto de 2026 · São Paulo
                </p>
                <Link
                  to="/macd-connect"
                  data-testid="live-macd-cta"
                  className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.14em] text-[#0a0a0a] border-b border-[#0a0a0a] pb-1 hover:text-[#F5B301] hover:border-[#F5B301] transition-colors"
                >
                  Conhecer o MACD Connect
                  <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  // ============================================================
  // Landing view
  // ============================================================
  return (
    <div ref={rootRef} data-testid="live-page" className="bg-white text-[#0a0a0a]">
      <Navbar />

      <main className="pt-24 md:pt-28">
        {/* HERO ------------------------------------------------------ */}
        <section
          data-testid="live-hero"
          className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16 md:pb-24"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Poster art — dominant on desktop, top on mobile */}
            <div className="lg:col-span-6 order-1 lg:order-1">
              <div
                data-testid="live-hero-art"
                className="relative w-full aspect-[9/16] max-w-[560px] mx-auto lg:mx-0 shadow-[0_30px_90px_-30px_rgba(0,0,0,0.35)] bg-[#0a0a0a]"
              >
                <img
                  src={LIVE_ART}
                  alt="Live Oasys Academy 10 AGO 20H — Crescimento não é só meta. É padrão. Gilson Ribeiro + Luciano Marques."
                  loading="eager"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Text + CTA */}
            <div className="lg:col-span-6 order-2 lg:order-2">
              <div className="reveal">
                <div className="flex items-center gap-3 mb-6">
                  <span
                    aria-hidden="true"
                    className="inline-block w-10 h-[3px] bg-[#F5B301]"
                  />
                  <span
                    data-testid="live-eyebrow"
                    className="text-xs uppercase tracking-[0.18em] text-[#0a0a0a]/70 font-medium"
                  >
                    Live · 10 AGO · 20H
                  </span>
                </div>

                <h1
                  data-testid="live-headline"
                  className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight"
                >
                  Crescimento não é <br />
                  <em className="italic">só meta.</em>{" "}
                  <span className="whitespace-nowrap">É padrão.</span>
                </h1>

                <p
                  data-testid="live-subheadline"
                  className="mt-6 text-lg md:text-xl text-[#2a2a2a] max-w-xl leading-[1.55]"
                >
                  Antes de mudar seus resultados, observe o que você repete.
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px] uppercase tracking-[0.14em] text-[#0a0a0a]/80">
                  <span className="font-medium">Gilson Ribeiro</span>
                  <span className="text-[#0a0a0a]/40">+</span>
                  <span className="font-medium">Luciano Marques</span>
                </div>

                <p className="mt-6 max-w-xl text-[15px] md:text-base text-[#0a0a0a]/70 leading-[1.6]">
                  Uma conversa ao vivo sobre comportamento, decisões e os padrões
                  que sustentam — ou limitam — o crescimento profissional.
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <span
                    data-testid="live-badge-free"
                    className="inline-flex items-center gap-2 bg-[#F5B301] text-[#0a0a0a] px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold"
                  >
                    Participação gratuita
                  </span>
                </div>

                <button
                  type="button"
                  data-testid="live-hero-cta"
                  onClick={scrollToForm}
                  className="mt-10 group inline-flex items-center gap-3 bg-[#0a0a0a] text-white px-8 py-4 font-medium tracking-wide hover:bg-[#F5B301] hover:text-[#0a0a0a] transition-colors duration-200"
                >
                  QUERO PARTICIPAR DA LIVE
                  <span
                    className="transition-transform duration-200 group-hover:translate-x-1"
                    aria-hidden="true"
                  >
                    →
                  </span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* FORM ------------------------------------------------------ */}
        <section
          ref={formRef}
          data-testid="live-form-section"
          className="bg-[#0a0a0a] text-white py-20 md:py-28"
        >
          <div className="max-w-[900px] mx-auto px-6 md:px-10">
            <div className="reveal">
              <div className="flex items-center gap-3 mb-6">
                <span
                  aria-hidden="true"
                  className="inline-block w-10 h-[3px] bg-[#F5B301]"
                />
                <span className="text-xs uppercase tracking-[0.18em] text-white/60 font-medium">
                  Inscreva-se gratuitamente
                </span>
              </div>

              <h2 className="font-display text-3xl md:text-5xl leading-[1.05] tracking-tight mb-4">
                Sua vaga na live.
              </h2>
              <p className="text-white/70 text-base md:text-lg max-w-xl mb-10 md:mb-12">
                Preenchimento em 40 segundos. Sem custo.
              </p>

              <form
                onSubmit={onSubmit}
                data-testid="live-form"
                className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6"
                noValidate
              >
                {/* Nome */}
                <div className="md:col-span-2">
                  <label className="block text-[11px] uppercase tracking-[0.14em] text-white/50 mb-2">
                    Nome completo
                  </label>
                  <input
                    data-testid="live-input-nome"
                    type="text"
                    autoComplete="name"
                    value={form.nome}
                    onChange={(e) => setField("nome", e.target.value)}
                    className="w-full bg-transparent border-b border-white/25 focus:border-[#F5B301] outline-none py-3 text-lg placeholder-white/30"
                    placeholder="Como você quer ser chamado"
                  />
                  {errors.nome && (
                    <p className="text-[#F5B301] text-xs mt-2">{errors.nome}</p>
                  )}
                </div>

                {/* WhatsApp */}
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.14em] text-white/50 mb-2">
                    WhatsApp
                  </label>
                  <input
                    data-testid="live-input-whatsapp"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={form.whatsapp}
                    onChange={(e) =>
                      setField("whatsapp", formatWhatsapp(e.target.value))
                    }
                    className="w-full bg-transparent border-b border-white/25 focus:border-[#F5B301] outline-none py-3 text-lg placeholder-white/30"
                    placeholder="(11) 98765-4321"
                  />
                  {errors.whatsapp && (
                    <p className="text-[#F5B301] text-xs mt-2">
                      {errors.whatsapp}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.14em] text-white/50 mb-2">
                    E-mail
                  </label>
                  <input
                    data-testid="live-input-email"
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    className="w-full bg-transparent border-b border-white/25 focus:border-[#F5B301] outline-none py-3 text-lg placeholder-white/30"
                    placeholder="voce@dominio.com"
                  />
                  {errors.email && (
                    <p className="text-[#F5B301] text-xs mt-2">{errors.email}</p>
                  )}
                </div>

                {/* Perfil */}
                <div className="md:col-span-2">
                  <label className="block text-[11px] uppercase tracking-[0.14em] text-white/50 mb-3">
                    Perfil
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {PERFIL_OPTIONS.map((opt) => {
                      const active = form.perfil === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          data-testid={`live-perfil-${opt.value}`}
                          onClick={() => setField("perfil", opt.value)}
                          className={`px-5 py-2.5 text-sm border transition-colors duration-150 ${
                            active
                              ? "bg-[#F5B301] border-[#F5B301] text-[#0a0a0a] font-medium"
                              : "border-white/30 text-white/80 hover:border-white/60"
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {errors.perfil && (
                    <p className="text-[#F5B301] text-xs mt-2">
                      {errors.perfil}
                    </p>
                  )}
                </div>

                {/* Cidade */}
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.14em] text-white/50 mb-2">
                    Cidade
                  </label>
                  <input
                    data-testid="live-input-cidade"
                    type="text"
                    autoComplete="address-level2"
                    value={form.cidade}
                    onChange={(e) => setField("cidade", e.target.value)}
                    className="w-full bg-transparent border-b border-white/25 focus:border-[#F5B301] outline-none py-3 text-lg placeholder-white/30"
                    placeholder="Sua cidade"
                  />
                  {errors.cidade && (
                    <p className="text-[#F5B301] text-xs mt-2">
                      {errors.cidade}
                    </p>
                  )}
                </div>

                {/* Estado */}
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.14em] text-white/50 mb-2">
                    UF
                  </label>
                  <select
                    data-testid="live-input-estado"
                    value={form.estado}
                    onChange={(e) => setField("estado", e.target.value)}
                    className="w-full bg-transparent border-b border-white/25 focus:border-[#F5B301] outline-none py-3 text-lg text-white [&>option]:bg-[#0a0a0a]"
                  >
                    <option value="" disabled className="text-white/40">
                      Selecione
                    </option>
                    {UFS.map((uf) => (
                      <option key={uf} value={uf}>
                        {uf}
                      </option>
                    ))}
                  </select>
                  {errors.estado && (
                    <p className="text-[#F5B301] text-xs mt-2">
                      {errors.estado}
                    </p>
                  )}
                </div>

                {/* Submit */}
                <div className="md:col-span-2 mt-4">
                  {errors._submit && (
                    <p
                      data-testid="live-submit-error"
                      className="text-[#F5B301] text-sm mb-4"
                    >
                      {errors._submit}
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="live-submit-btn"
                    disabled={submitting}
                    className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#F5B301] text-[#0a0a0a] px-10 py-4 font-semibold tracking-wide hover:bg-white transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "ENVIANDO…" : "QUERO PARTICIPAR DA LIVE"}
                  </button>
                  <p className="mt-4 text-white/50 text-xs md:text-sm">
                    Ao se inscrever, você receberá as informações de acesso à
                    live.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
